import { Box, Button, Typography, Tabs, Tab, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { GadsWrapper } from "./styles";
import { useForm, Controller } from "react-hook-form";

import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { useApi } from "../../hooks/useApi.hook";
import { ControlForm, Modal } from "../../components";
import { toggleMessage } from "../../components/Toast/Toast";
import CompanyService from "../../services/api/company.service";
import { ICompany, IUpdateGoogleAds } from "../../models/company";
import ReportEmbed from "../../components/Embeb/ReportEmbed";
import variableStyles from "../../theme/variable-styles";
import { useTranslation } from "react-i18next";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`
  };
}

const GADS = () => {
  const [tabValue, setTabValue] = useState(0);
  const { t } = useTranslation();

  // States for GADS
  const [openCreateGads, setOpenCreateGads] = useState<boolean>(false);
  const getGoooleAds = useApi<ICompany>({ isFetch: true });
  const updateGAds = useApi<IUpdateGoogleAds>({});
  const [goooleAds, setGoooleAds] = useState<ICompany | undefined>(undefined);

  // States for GCSL
  const [openCreateGcsl, setOpenCreateGcsl] = useState<boolean>(false);
  const updateGcsl = useApi<IUpdateGoogleAds>({});

  function fetchData() {
    getGoooleAds.request(CompanyService.getGoogleAds());
  }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setGoooleAds(getGoooleAds.data);
    setValueGads("iframeGoogleAds", getGoooleAds.data?.iframeGoogleAds);
    setValueGcsl("iframeGoogleReport", getGoooleAds.data?.iframeGoogleReport);
  }, [getGoooleAds.data]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Validation schema for GADS
  const validationSchemaGads = Yup.object().shape({
    iframeGoogleAds: Yup.string().required(t("Gads.Validate.IframeRequired"))
  }) as any;

  const {
    control: controlGads,
    handleSubmit: handleSubmitGads,
    reset: resetGads,
    setValue: setValueGads,
    formState: { errors: errorsGads }
  } = useForm<ICompany>({
    resolver: yupResolver(validationSchemaGads)
  });

  // Validation schema for GCSL
  const validationSchemaGcsl = Yup.object().shape({
    iframeGoogleReport: Yup.string().required(t("Gcsl.Validate.IframeRequired"))
  }) as any;

  const {
    control: controlGcsl,
    handleSubmit: handleSubmitGcsl,
    reset: resetGcsl,
    setValue: setValueGcsl,
    formState: { errors: errorsGcsl }
  } = useForm<ICompany>({
    resolver: yupResolver(validationSchemaGcsl)
  });

  const onSubmitGads = (request: ICompany) => {
    const req: IUpdateGoogleAds = {
      iframeGoogleAds: request.iframeGoogleAds,
      type: 0
    };
    updateGAds.request(
      CompanyService.updateGoogleAds(req).then(res => {
        if (res.success) {
          toggleMessage({
            type: "success",
            message: res.message ?? ""
          });
          fetchData();
          setOpenCreateGads(false);
          resetGads();
        }
      })
    );
  };

  const onSubmitGcsl = (request: ICompany) => {
    const req: IUpdateGoogleAds = {
      iframeGoogleReport: request.iframeGoogleReport,
      type: 1
    };
    updateGcsl.request(
      CompanyService.updateGoogleAds(req).then(res => {
        if (res.success) {
          toggleMessage({
            type: "success",
            message: res.message ? res.message : ""
          });
          fetchData();
          setOpenCreateGcsl(false);
          resetGcsl();
        }
      })
    );
  };

  return (
    <GadsWrapper>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="gads gcsl tabs"
          >
            <Tab label="Google Ads" {...a11yProps(0)} />
            <Tab label="Google Console" {...a11yProps(1)} />
          </Tabs>
        </Box>

        {/* Tab Panel for GADS */}
        <TabPanel value={tabValue} index={0}>
          <Box>
            <div>
              <Button
                variant="contained"
                size="small"
                aria-label="aria-modal"
                onClick={() => {
                  setOpenCreateGads(true);
                }}
                sx={{
                  marginBottom: "20px",
                  backgroundColor: variableStyles.MainColor
                }}
              >
                <Typography className="subtitle2-text" variant="subtitle2">
                  {t("Gads.Button.Update")}
                </Typography>
              </Button>

              <Modal
                title={t("Gads.Modal.Title")}
                textSubmit={t("Modal.SubmitText")}
                textClose={t("Modal.CancelText")}
                open={openCreateGads}
                onSubmit={handleSubmitGads(onSubmitGads)}
                onClose={() => {
                  setOpenCreateGads(false);
                  resetGads();
                }}
                loadingState={updateGAds.loading}
              >
                <form
                  onSubmit={handleSubmitGads(onSubmitGads)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px"
                  }}
                >
                  <ControlForm
                    title="Google Ads"
                    isRequired
                    classname="brand-form"
                  >
                    <Controller
                      name="iframeGoogleAds"
                      control={controlGads}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={4}
                          placeholder="Google Ads"
                          size="small"
                          error={!!errorsGads?.iframeGoogleAds}
                          helperText={errorsGads?.iframeGoogleAds?.message}
                        />
                      )}
                    />
                  </ControlForm>
                </form>
              </Modal>
            </div>
          </Box>
          <Box
            sx={{
              "& iframe ": {
                width: "100%",
                height: "750px"
              }
            }}
          >
            {goooleAds?.iframeGoogleAds && (
              <ReportEmbed iframeCode={goooleAds?.iframeGoogleAds} />
            )}
          </Box>
        </TabPanel>

        {/* Tab Panel for GCSL */}
        <TabPanel value={tabValue} index={1}>
          <Box>
            <div>
              <Button
                variant="contained"
                size="small"
                aria-label="aria-modal"
                onClick={() => {
                  setOpenCreateGcsl(true);
                }}
                sx={{
                  marginBottom: "20px",
                  backgroundColor: variableStyles.MainColor
                }}
              >
                <Typography className="subtitle2-text" variant="subtitle2">
                  {t("Gcsl.Button.Update")}
                </Typography>
              </Button>

              <Modal
                title={t("Gcsl.Modal.Title")}
                textSubmit={t("Modal.SubmitText")}
                textClose={t("Modal.CancelText")}
                open={openCreateGcsl}
                onSubmit={handleSubmitGcsl(onSubmitGcsl)}
                onClose={() => {
                  setOpenCreateGcsl(false);
                  resetGcsl();
                }}
                loadingState={updateGcsl.loading}
              >
                <form
                  onSubmit={handleSubmitGcsl(onSubmitGcsl)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px"
                  }}
                >
                  <ControlForm
                    title="Google Console"
                    isRequired
                    classname="brand-form"
                  >
                    <Controller
                      name="iframeGoogleReport"
                      control={controlGcsl}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={4}
                          placeholder="Google Console"
                          size="small"
                          error={!!errorsGcsl?.iframeGoogleReport}
                          helperText={errorsGcsl?.iframeGoogleReport?.message}
                        />
                      )}
                    />
                  </ControlForm>
                </form>
              </Modal>
            </div>
          </Box>
          <Box
            sx={{
              "& iframe ": {
                width: "100%",
                height: "750px"
              }
            }}
          >
            {goooleAds?.iframeGoogleReport && (
              <ReportEmbed iframeCode={goooleAds?.iframeGoogleReport} />
            )}
          </Box>
        </TabPanel>
      </Box>
    </GadsWrapper>
  );
};

export default GADS;
